/**
 * Экран генерации. Стадии приходят по SSE, при обрыве потока watcher
 * переключается на опрос GET /projects/:id раз в 2 секунды — экран об этом
 * честно пишет. Приложение можно закрыть: результат придёт сообщением от бота.
 */
import type { ReactElement } from 'react';
import { PROGRESS_STAGES } from '@reels/shared/constants';
import type { ProjectStatus } from '@reels/shared';
import { ActionButton } from '../components/ActionButton';
import { ErrorState, OfflineBanner } from '../components/States';
import * as telegram from '../lib/telegram';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';
import { t } from '../i18n';


const stageTitle = (stage: ProjectStatus): string => {
  switch (stage) {
    case 'uploading': return t('progress.stage.uploading');
    case 'transcribing': return t('progress.stage.transcribing');
    case 'analyzing': return t('progress.stage.analyzing');
    case 'planning': return t('progress.stage.planning');
    case 'rendering': return t('progress.stage.rendering');
    case 'delivering': return t('progress.stage.delivering');
    default: return stage;
  }
};

const VISIBLE_STAGES: ProjectStatus[] = PROGRESS_STAGES.filter((stage) => stage !== 'done');

export function Progress(): ReactElement {
  const { state, dispatch } = useStore();
  const actions = useActions();
  const project = state.project;
  if (!project) {
    return (
      <ErrorState
        message={t('progress.notFound')}
        retryLabel={t('progress.toProjects')}
        onRetry={() => dispatch({ type: 'route', screen: 'projects' })}
      />
    );
  }

  const currentIndex = VISIBLE_STAGES.indexOf(project.status);
  const failed = project.status === 'failed';
  const percent = state.stage?.progress ?? 0;
  const stageNumber = Math.max(currentIndex, 0) + 1;
  const overall = Math.max(0, Math.min(100, Math.round(percent)));

  return (
    <>
      <h1>
        {failed ? (
          t('progress.failed')
        ) : (
          <>
            {t('progress.title.lead')}<span className="accent">{t('progress.title.accent')}</span>
          </>
        )}
      </h1>
      {!state.online ? <OfflineBanner /> : null}

      {failed ? (
        <ErrorState
          message={project.error ?? t('progress.failedDefault')}
          retryLabel={t('progress.restart')}
          onRetry={() => void actions.startGeneration()}
        />
      ) : null}

      {!failed ? (
        <div className="facts">
          <div className="fact">
            <span className="fact-label">{t('progress.fact.stage')}</span>
            <span className="fact-value">
              {stageNumber}
              <small>{t('progress.fact.of', { total: VISIBLE_STAGES.length })}</small>
            </span>
          </div>
          <div className="fact">
            <span className="fact-label">{t('progress.fact.done')}</span>
            <span className="fact-value">{overall}%</span>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="stages">
          {VISIBLE_STAGES.map((stage, index) => {
            const done = currentIndex > index || project.status === 'done';
            const active = currentIndex === index && !failed;
            const className = failed && active ? 'stage failed' : done ? 'stage done' : active ? 'stage active' : 'stage';
            return (
              <div key={stage} className={className}>
                <span className="dot">{done ? '✓' : index + 1}</span>
                <div className="col grow">
                  <span>{stageTitle(stage)}</span>
                  {active && !failed ? (
                    <span className="small muted">
                      {state.stage?.totalFiles ? t('progress.files', { done: state.stage.completedFiles ?? 0, total: state.stage.totalFiles }) : percent > 0 ? `${Math.round(percent)}%` : t('progress.working')}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        {!failed ? (
          <div className="bar">
            <span style={{ width: `${overall}%` }} />
          </div>
        ) : null}
      </div>

      <div className="banner info">
        {t('progress.closeHint')}
      </div>

      <span className="small muted center">
        {state.streamMode === 'poll'
          ? t('progress.stream.poll')
          : t('progress.stream.live')}
      </span>

      <button
        type="button"
        className="btn ghost"
        onClick={() => dispatch({ type: 'route', screen: 'projects' })}
      >
        {t('progress.allProjects')}
      </button>

      <ActionButton
        text={t('progress.minimize')}
        onClick={() => telegram.close()}
        visible={!failed && telegram.isTelegram}
      />
    </>
  );
}
